import React, { useRef, useEffect, useMemo, useState, useCallback } from "react";
import "./MobileMonthView.css";

// ─── Constants ────────────────────────────────────────────────
const DAY_HEADERS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// ─── Pure helpers ─────────────────────────────────────────────
function toDateStr(date) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function parseDateOnlyLocal(value) {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  return new Date(year, month - 1, day, 0, 0, 0, 0);
}

function parseEventBoundary(value) {
  const localDateOnly = parseDateOnlyLocal(value);
  if (localDateOnly) return localDateOnly;

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
}

function datePartFromValue(value) {
  const match = String(value || "").match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : "";
}

function isAllDayEvent(event) {
  if (event?.allDay === true) return true;
  if (event?.extendedProps?.all_day === true) return true;
  if (event?.extendedProps?.allDay === true) return true;
  return String(event?.start || "").length === 10;
}

function addDays(date, dayCount) {
  const next = new Date(date);
  next.setDate(next.getDate() + dayCount);
  return next;
}

function isSameDay(d1, d2) {
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
}

/**
 * Build an array of week rows spanning from the Monday on or before
 * `startDate` to the Sunday on or after `endDate`.
 * Each week row: [{ date, inRange }] × 7 (Mon→Sun)
 */
function buildWeekRows(startDate, endDate) {
  // Find the Monday at or before startDate
  const cursor = new Date(startDate);
  const dow = cursor.getDay(); // 0=Sun,1=Mon...6=Sat
  const offset = dow === 0 ? -6 : 1 - dow; // shift to Monday
  cursor.setDate(cursor.getDate() + offset);

  const rows = [];
  while (cursor <= endDate || rows.length === 0) {
    const week = [];
    for (let d = 0; d < 7; d++) {
      week.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    rows.push(week);
    // ensure we've covered the endDate (Sunday of that week)
    if (cursor > endDate && rows.length > 0) break;
  }
  return rows;
}

// ─── Component ────────────────────────────────────────────────
/**
 * MobileMonthView
 *
 * Props:
 *  - events: normalised event array from useEvents()
 *  - onDayPress: (date: Date) => void  — called when a day cell is tapped
 *  - today: Date (optional) — defaults to new Date()
 */
function MobileMonthView({
  events = [],
  onDayPress,
  today = new Date(),
  anchorDate = null,
  todayToken = 0,
  onVisibleRangeChange = null,
  rangeStartMonths = -240,
  rangeEndMonths = 240,
}) {
  const scrollRef = useRef(null);
  const initialAnchorRef = useRef(
    anchorDate instanceof Date && !Number.isNaN(anchorDate.getTime()) ? anchorDate : null
  );
  const [rangeStart] = useState(rangeStartMonths);
  const [rangeEnd] = useState(rangeEndMonths);

  const scrollToMonth = useCallback((date, behavior) => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    const targetDate =
      date instanceof Date && !Number.isNaN(date.getTime()) ? date : new Date();
    const year = targetDate.getFullYear();
    const month = targetDate.getMonth();
    const monthEl = scroller.querySelector(`.mmv-month[data-year="${year}"][data-month="${month}"]`);
    if (!monthEl) return;
    const monthLabel = monthEl.querySelector(".mmv-month-label");
    const anchorTop = monthLabel ? monthLabel.offsetTop : monthEl.offsetTop;
    const targetTop = Math.max(0, anchorTop - 28);
    scroller.scrollTo({ top: targetTop, behavior });
  }, []);

  const reportVisibleRange = useMemo(() => {
    return () => {
      if (typeof onVisibleRangeChange !== "function") return;
      const scroller = scrollRef.current;
      if (!scroller) return;

      const monthEls = Array.from(scroller.querySelectorAll(".mmv-month[data-year][data-month]"));
      if (monthEls.length === 0) return;

      const viewportTop = scroller.scrollTop;
      const viewportBottom = viewportTop + scroller.clientHeight;
      const visibleMonths = monthEls.filter((el) => {
        const top = el.offsetTop;
        const bottom = top + el.offsetHeight;
        return bottom >= viewportTop && top <= viewportBottom;
      });
      const rangeMonths = visibleMonths.length > 0 ? visibleMonths : [monthEls[0], monthEls[monthEls.length - 1]];
      const first = rangeMonths[0];
      const last = rangeMonths[rangeMonths.length - 1];
      const startDate = new Date(Number(first.dataset.year), Number(first.dataset.month), 1);
      const endDate = new Date(Number(last.dataset.year), Number(last.dataset.month) + 1, 0);
      onVisibleRangeChange(startDate, endDate);
    };
  }, [onVisibleRangeChange]);

  const months = useMemo(() => {
    const result = [];
    for (let offset = rangeStart; offset <= rangeEnd; offset++) {
      const d = new Date(today.getFullYear(), today.getMonth() + offset, 1);
      result.push({ year: d.getFullYear(), month: d.getMonth() });
    }
    return result;
  }, [today, rangeStart, rangeEnd]);

  // Event lookup map: "YYYY-MM-DD" → events[]
  const eventsByDate = useMemo(() => {
    const map = new Map();

    const push = (key, event) => {
      if (!key) return;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(event);
    };

    for (const ev of events) {
      if (!ev.start) continue;

      if (isAllDayEvent(ev)) {
        const startPart = datePartFromValue(ev.start);
        const start = parseDateOnlyLocal(startPart);
        if (!start) continue;

        const endPart = datePartFromValue(ev.end);
        let endExclusive = endPart ? parseDateOnlyLocal(endPart) : null;
        if (!endExclusive || endExclusive <= start) {
          endExclusive = addDays(start, 1);
        }

        for (let cursor = new Date(start); cursor < endExclusive; cursor = addDays(cursor, 1)) {
          push(toDateStr(cursor), ev);
        }
        continue;
      }

      const start = parseEventBoundary(ev.start);
      if (!start) continue;
      push(toDateStr(start), ev);
    }
    return map;
  }, [events]);

  // Scroll to today's month on mount
  useEffect(() => {
    const run = () => {
      scrollToMonth(initialAnchorRef.current, "auto");
      reportVisibleRange();
    };
    run();
    const frame = requestAnimationFrame(run);
    const timer = window.setTimeout(run, 60);
    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (todayToken === 0) return;
    const run = () => {
      scrollToMonth(new Date(), "smooth");
      reportVisibleRange();
    };
    run();
    const frame = requestAnimationFrame(run);
    const timer = window.setTimeout(run, 60);
    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, [todayToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const commitDayTap = (date) => {
    if (!onDayPress) return;
    onDayPress(date);
  };

  const renderMonth = ({ year, month }) => {
    const isCurrentMonth =
      year === today.getFullYear() && month === today.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const weekRows = buildWeekRows(firstDay, lastDay);

    return (
      <div key={`${year}-${month}`} className="mmv-month" data-year={year} data-month={month}>
        {/* Month label row */}
        <div className={`mmv-month-label${isCurrentMonth ? " mmv-month-label--current" : ""}`}>
          {MONTH_NAMES[month]} {year}
        </div>

        {/* Week rows */}
        {weekRows.map((week, wi) => {
          const inMonth = week.some((d) => d.getMonth() === month);
          if (!inMonth) return null; // skip trailing overflow rows

          return (
            <div key={wi} className="mmv-week-row">
              {/* Day cells */}
              {week.map((date, di) => {
                const dateStr = toDateStr(date);
                const isToday = isSameDay(date, today);
                const isThisMonth = date.getMonth() === month;
                const isWeekend = di >= 5; // Sat or Sun (Mon-indexed)
                const dayEvents = eventsByDate.get(dateStr) || [];

                const cellClasses = [
                  "mmv-day",
                  isToday ? "mmv-day--today" : "",
                  !isThisMonth ? "mmv-day--other-month" : "",
                  isWeekend ? "mmv-day--weekend" : "",
                ]
                  .filter(Boolean)
                  .join(" ");

                return (
                  <div
                    key={di}
                    className={cellClasses}
                    role="button"
                    tabIndex={0}
                    onClick={() => commitDayTap(date)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        commitDayTap(date);
                      }
                    }}
                  >
                    {/* Day number */}
                    <div className="mmv-day-num-wrap">
                      <button
                        type="button"
                        className={`mmv-day-num-btn${isToday ? " mmv-day-num-btn--today" : ""}`}
                        onClick={(event) => event.preventDefault()}
                        aria-label={`Open ${dateStr}`}
                        tabIndex={-1}
                      >
                        <span className={`mmv-day-num${isToday ? " mmv-day-num--today" : ""}`}>
                          {date.getDate()}
                        </span>
                      </button>
                    </div>

                    {/* Event chips (max 2, then "+N more") */}
                    <div className="mmv-chips">
                      {dayEvents.slice(0, 2).map((ev) => {
                        const color = String(
                          ev.extendedProps?.category_color || ""
                        ).trim();
                        const hasColor = /^#[0-9A-Fa-f]{6}$/.test(color);
                        const billingStatus = String(ev.extendedProps?.billing_status || "").trim().toLowerCase();
                        const isLesson = ev.extendedProps?.is_lesson === true;
                        const useCategoryTint = hasColor && !(isLesson && (billingStatus === "billed" || billingStatus === "paid"));
                        const chipClass = [
                          "mmv-chip",
                          isLesson ? "mmv-chip--lesson" : "",
                          isLesson && billingStatus === "billed" ? "mmv-chip--lesson-billed" : "",
                          isLesson && billingStatus === "paid" ? "mmv-chip--lesson-paid" : "",
                          ev.extendedProps?.no_show ? "mmv-chip--noshow" : "",
                        ]
                          .filter(Boolean)
                          .join(" ");

                        return (
                          <div
                            key={ev.id}
                            className={chipClass}
                            style={
                              useCategoryTint
                                ? { backgroundColor: color, borderColor: color }
                                : {}
                            }
                          >
                            {ev.title}
                          </div>
                        );
                      })}
                      {dayEvents.length > 2 && (
                        <div className="mmv-chip mmv-chip--overflow">
                          +{dayEvents.length - 2} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="mmv-wrapper">
      {/* Fixed day-of-week header */}
      <div className="mmv-dow-header" aria-hidden="true">
        {DAY_HEADERS.map((d, i) => (
          <div
            key={i}
            className={`mmv-dow-label${i >= 5 ? " mmv-dow-label--weekend" : ""}`}
          >
            {d}
          </div>
        ))}
      </div>

      {/* Scrollable month grid */}
      <div className="mmv-scroll" ref={scrollRef} onScroll={reportVisibleRange}>
        {months.map(renderMonth)}
      </div>
    </div>
  );
}

export default MobileMonthView;
